import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Link, Copy, Check, Settings, Globe } from 'lucide-react';
import { supabase, TABLES } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import logger from '../../utils/logger';

const AvailabilitySettings = () => {
  const { currentUser } = useAuth();
  const [availability, setAvailability] = useState({
    workingHours: {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    },
    slotDuration: 30,
    bufferTime: 15,
    maxAdvanceBooking: 30, // days
    minAdvanceBooking: 1, // hours
    bookingPageTitle: 'Schedule a Meeting',
    bookingPageDescription: 'Choose a time that works for you',
    isPublicBookingEnabled: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Load existing settings
  useEffect(() => {
    loadAvailabilitySettings();
  }, [currentUser]);

  const loadAvailabilitySettings = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('booking_settings')
        .select('*')
        .eq('user_id', currentUser.uid)
        .single();
      
      if (data && !error) {
        setAvailability({
          workingHours: data.working_hours || availability.workingHours,
          slotDuration: data.slot_duration || 30,
          bufferTime: data.buffer_time || 15,
          maxAdvanceBooking: data.max_advance_booking || 30,
          minAdvanceBooking: data.min_advance_booking || 1,
          bookingPageTitle: data.booking_page_title || 'Schedule a Meeting',
          bookingPageDescription: data.booking_page_description || 'Choose a time that works for you',
          isPublicBookingEnabled: data.is_enabled !== false
        });
      }
    } catch (err) {
      logger.warn('Error loading availability settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveAvailabilitySettings = async () => {
    if (!currentUser) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const settingsData = {
        user_id: currentUser.uid,
        working_hours: availability.workingHours,
        slot_duration: availability.slotDuration,
        buffer_time: availability.bufferTime,
        max_advance_booking: availability.maxAdvanceBooking,
        min_advance_booking: availability.minAdvanceBooking,
        booking_page_title: availability.bookingPageTitle,
        booking_page_description: availability.bookingPageDescription,
        is_enabled: availability.isPublicBookingEnabled,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('booking_settings')
        .upsert(settingsData, { onConflict: 'user_id' });
      
      if (error) throw error;
      
      setSuccess('Availability settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      logger.error('Error saving availability settings:', err);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy:', err);
    }
  };

  const getBookingUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/booking`;
  };

  const getEmbedCode = () => {
    const bookingUrl = getBookingUrl();
    return `<iframe src="${bookingUrl}" width="400" height="700" frameborder="0" style="border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"></iframe>`;
  };

  const dayNames = {
    monday: 'Monday',
    tuesday: 'Tuesday', 
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday'
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Public Booking Settings
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Configure your public booking calendar and availability
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={availability.isPublicBookingEnabled}
                onChange={(e) => setAvailability(prev => ({ ...prev, isPublicBookingEnabled: e.target.checked }))}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Enable Public Booking
              </span>
            </label>
          </div>
        </div>
        
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm flex items-center">
              <Check className="w-4 h-4 mr-2" />
              {success}
            </p>
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Working Hours */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="w-4 h-4 mr-2" />
          Working Hours
        </h4>
        
        <div className="space-y-3">
          {Object.entries(availability.workingHours).map(([day, hours]) => (
            <div key={day} className="flex items-center space-x-4">
              <div className="w-24">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={hours.enabled}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      workingHours: {
                        ...prev.workingHours,
                        [day]: { ...hours, enabled: e.target.checked }
                      }
                    }))}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {dayNames[day]}
                  </span>
                </label>
              </div>
              
              {hours.enabled && (
                <div className="flex items-center space-x-2">
                  <input
                    type="time"
                    value={hours.start}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      workingHours: {
                        ...prev.workingHours,
                        [day]: { ...hours, start: e.target.value }
                      }
                    }))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="time"
                    value={hours.end}
                    onChange={(e) => setAvailability(prev => ({
                      ...prev,
                      workingHours: {
                        ...prev.workingHours,
                        [day]: { ...hours, end: e.target.value }
                      }
                    }))}
                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Booking Settings */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Settings className="w-4 h-4 mr-2" />
          Booking Configuration
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Duration (minutes)
            </label>
            <select
              value={availability.slotDuration}
              onChange={(e) => setAvailability(prev => ({ ...prev, slotDuration: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 hour</option>
              <option value={90}>1.5 hours</option>
              <option value={120}>2 hours</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buffer Time (minutes)
            </label>
            <select
              value={availability.bufferTime}
              onChange={(e) => setAvailability(prev => ({ ...prev, bufferTime: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>No buffer</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Advance Booking (days)
            </label>
            <select
              value={availability.maxAdvanceBooking}
              onChange={(e) => setAvailability(prev => ({ ...prev, maxAdvanceBooking: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>1 week</option>
              <option value={14}>2 weeks</option>
              <option value={30}>1 month</option>
              <option value={60}>2 months</option>
              <option value={90}>3 months</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Notice (hours)
            </label>
            <select
              value={availability.minAdvanceBooking}
              onChange={(e) => setAvailability(prev => ({ ...prev, minAdvanceBooking: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>1 hour</option>
              <option value={2}>2 hours</option>
              <option value={4}>4 hours</option>
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
            </select>
          </div>
        </div>
      </div>

      {/* Page Customization */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
          <Globe className="w-4 h-4 mr-2" />
          Booking Page Customization
        </h4>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Page Title
            </label>
            <input
              type="text"
              value={availability.bookingPageTitle}
              onChange={(e) => setAvailability(prev => ({ ...prev, bookingPageTitle: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Schedule a Meeting"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Page Description
            </label>
            <textarea
              value={availability.bookingPageDescription}
              onChange={(e) => setAvailability(prev => ({ ...prev, bookingPageDescription: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Choose a time that works for you"
            />
          </div>
        </div>
      </div>

      {/* Public Links */}
      {availability.isPublicBookingEnabled && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
            <Link className="w-4 h-4 mr-2" />
            Public Booking Links
          </h4>
          
          <div className="space-y-4">
            {/* Direct Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🌐 Direct Booking Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={getBookingUrl()}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm font-mono"
                />
                <button
                  onClick={() => copyToClipboard(getBookingUrl())}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Share this link directly or add it to your email signature
              </p>
            </div>
            
            {/* Embed Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🔗 Website Embed Code
              </label>
              <div className="flex items-start space-x-2">
                <textarea
                  value={getEmbedCode()}
                  readOnly
                  rows={3}
                  className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-xs font-mono resize-none"
                />
                <button
                  onClick={() => copyToClipboard(getEmbedCode())}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Copy and paste this code into your website to embed the booking widget
              </p>
            </div>
          </div>
          
          {/* Preview */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-2">
              <Globe className="w-4 h-4 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-900">Preview</span>
            </div>
            <p className="text-sm text-blue-800 mb-3">
              Your public booking page is live and ready to use!
            </p>
            <a
              href={getBookingUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Globe className="w-4 h-4 mr-2" />
              View Booking Page
            </a>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveAvailabilitySettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AvailabilitySettings;